import { useState, useMemo, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { Badge } from '../shared/components/Badge';
import { Tooltip } from '../shared/components/Tooltip';
import { getStatusInfo } from '../shared/utils/inventoryCalculations';
import { ProductWithCalculations, StatusType } from '../shared/types/inventory';
import { Search, ArrowUpDown, ArrowUp, ArrowDown, X, ChevronDown, ChevronRight, Building2 } from 'lucide-react';
import { StatusCards } from './StatusCards';
import { ProductThumbnail } from '../shared/components/ProductThumbnail';

type SortField = 'nome' | 'estoqueAtual' | 'cmd' | 'ss' | 'rop' | 'cobertura' | 'estoqueAlvo' | 'pedidoSugerido' | 'status';
type SortDirection = 'asc' | 'desc';

const STATUS_PRIORITY: Record<StatusType, number> = {
  ruptura: 1,
  sem_venda: 5,
  iminente: 2,
  minimo: 3,
  critica: 4,
  excesso: 6,
  saudavel: 7,
};

const DEFAULT_COLUMN_WIDTHS: Record<SortField, number> = {
  nome: 240, estoqueAtual: 120, cmd: 90, ss: 90, rop: 90,
  cobertura: 130, estoqueAlvo: 120, pedidoSugerido: 140, status: 130,
};
const MIN_COLUMN_WIDTHS: Record<SortField, number> = {
  nome: 150, estoqueAtual: 90, cmd: 70, ss: 70, rop: 70,
  cobertura: 100, estoqueAlvo: 100, pedidoSugerido: 115, status: 105,
};

interface CNPJSectionProps {
  cnpj: string;
  razaoSocial: string;
  products: ProductWithCalculations[];
  isExpanded: boolean;
  onToggle: () => void;
  onSelectProduct: (product: ProductWithCalculations) => void;
}

function formatCNPJ(cnpj: string): string {
  const digits = cnpj.replace(/\D/g, '');
  if (digits.length !== 14) return cnpj;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

function ProductSignals({product}: {product: ProductWithCalculations}) {
  const signals = [
    (product.saidaPerdas ?? 0) > 0 && {label: `Perdas ${formatNumberCompact(product.saidaPerdas ?? 0)} un`, className: 'bg-red-50 text-red-700'},
    Math.abs(product.divergenciaEstoque ?? 0) > 0 && {label: `Divergência ${formatNumberCompact(product.divergenciaEstoque ?? 0)} un`, className: 'bg-amber-50 text-amber-700'},
    product.otifPct !== undefined && product.otifPct < 95 && {label: `OTIF ${product.otifPct.toFixed(1)}%`, className: 'bg-violet-50 text-violet-700'},
  ].filter((signal): signal is {label: string; className: string} => Boolean(signal));

  if (!signals.length) return null;
  return (
    <span className="mt-1 flex flex-wrap gap-1">
      {signals.map((signal) => (
        <span key={signal.label} className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${signal.className}`}>{signal.label}</span>
      ))}
    </span>
  );
}

function formatNumberCompact(value: number) {
  return new Intl.NumberFormat('pt-BR', {maximumFractionDigits: 1}).format(value);
}

export const CNPJSection = ({ cnpj, razaoSocial, products, isExpanded, onToggle, onSelectProduct }: CNPJSectionProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [activeStatus, setActiveStatus] = useState<StatusType | null>(null);
  const [columnWidths, setColumnWidths] = useState<Record<SortField, number>>(DEFAULT_COLUMN_WIDTHS);

  const statusCounts = useMemo(() => {
    const counts: Record<StatusType, number> = {
      ruptura: 0, sem_venda: 0, iminente: 0, minimo: 0, critica: 0, excesso: 0, saudavel: 0,
    };
    products.forEach((p) => { counts[p.status]++; });
    return counts;
  }, [products]);

  const filteredAndSorted = useMemo(() => {
    let result = [...products];

    if (activeStatus) {
      result = result.filter((p) => p.status === activeStatus);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (p) => p.nome.toLowerCase().includes(q) || p.ean.toLowerCase().includes(q)
      );
    }

    if (sortField) {
      result.sort((a, b) => {
        let valA: number | string;
        let valB: number | string;

        if (sortField === 'nome') {
          valA = a.nome.toLowerCase();
          valB = b.nome.toLowerCase();
        } else if (sortField === 'status') {
          valA = STATUS_PRIORITY[a.status];
          valB = STATUS_PRIORITY[b.status];
        } else {
          valA = a[sortField];
          valB = b[sortField];
        }

        if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [products, searchQuery, sortField, sortDirection, activeStatus]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortField(null);
        setSortDirection('asc');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const startResize = (field: SortField, event: ReactMouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = columnWidths[field];
    const onMove = (moveEvent: MouseEvent) => {
      const nextWidth = Math.max(MIN_COLUMN_WIDTHS[field], startWidth + moveEvent.clientX - startX);
      setColumnWidths((current) => ({ ...current, [field]: nextWidth }));
    };
    const onEnd = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
  };
  const handleStatusClick = (status: StatusType) => {
    setActiveStatus((prev) => (prev === status ? null : status));
  };

  const formatNumber = (num: number, decimals: number = 2) => num.toFixed(decimals);

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3.5 h-3.5 text-gray-400 ml-1 flex-shrink-0" />;
    if (sortDirection === 'asc') return <ArrowUp className="w-3.5 h-3.5 text-teal-600 ml-1 flex-shrink-0" />;
    return <ArrowDown className="w-3.5 h-3.5 text-teal-600 ml-1 flex-shrink-0" />;
  };

  const SortableHeader = ({ field, children, align = 'left' }: { field: SortField; children: ReactNode; align?: 'left' | 'right' }) => (
    <th
      style={{ width: columnWidths[field] }}
      onClick={() => handleSort(field)}
      className={`relative select-none px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-700 transition-colors hover:bg-gray-100 ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
      <div className={`flex cursor-pointer items-center ${align === 'right' ? 'justify-end' : ''}`}>
        {children}<SortIcon field={field} />
      </div>
      <span
        role="separator"
        aria-label={`Redimensionar coluna ${field}`}
        title="Arraste para redimensionar"
        onMouseDown={(event) => startResize(field, event)}
        onClick={(event) => event.stopPropagation()}
        className="absolute right-0 top-0 h-full w-2 cursor-col-resize border-r border-transparent hover:border-teal-400"
      />
    </th>
  );

  const tableWidth = Object.values(columnWidths).reduce((sum, width) => sum + width, 0);
  const urgentCount = statusCounts.ruptura + statusCounts.iminente;
  const healthyCount = statusCounts.saudavel;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-visible transition-all duration-200">
      <button
        onClick={onToggle}
        className="flex w-full items-start justify-between gap-2 px-3 py-4 text-left transition-colors hover:bg-gray-50 sm:items-center sm:px-6"
      >
        <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-4">
          <div className="flex flex-none items-center gap-2 text-gray-500">
            {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </div>
          <div className="hidden flex-none rounded-lg border border-teal-100 bg-teal-50 p-2 min-[390px]:block">
            <Building2 className="w-5 h-5 text-teal-600" />
          </div>
          <div className="min-w-0 text-left">
            <h3 className="truncate font-mono text-sm font-bold tracking-wide text-gray-900 sm:text-base" title={formatCNPJ(cnpj)}>
              {formatCNPJ(cnpj)}
            </h3>
            {razaoSocial && (
              <p className="mt-0.5 truncate text-xs font-normal text-gray-500 sm:text-sm" title={razaoSocial}>
                {razaoSocial}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-0.5">
              {products.length} {products.length === 1 ? 'produto' : 'produtos'}
            </p>
          </div>
        </div>

        <div className="ml-1 flex flex-none flex-col items-end gap-1.5 sm:flex-row sm:items-center sm:gap-3">
          {urgentCount > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-red-100 bg-red-50 px-2 py-1 text-[11px] font-semibold text-red-700 sm:px-2.5 sm:text-xs">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
              <span className="sm:hidden">{urgentCount} urg.</span>
              <span className="hidden sm:inline">{urgentCount} urgente{urgentCount > 1 ? 's' : ''}</span>
            </span>
          )}
          {healthyCount > 0 && (
            <span className="hidden items-center gap-1.5 rounded-full border border-green-100 bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700 sm:inline-flex">
              {healthyCount} saudável{healthyCount > 1 ? '' : ''}
            </span>
          )}
          <span className="hidden text-sm font-medium text-gray-400 sm:inline">
            {products.length}
          </span>
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-gray-100">
          <div className="px-3 pb-2 pt-4 sm:px-6 sm:pt-5">
            <StatusCards
              counts={statusCounts}
              activeStatus={activeStatus}
              onStatusClick={handleStatusClick}
            />

            <div className="mb-4 flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-3">
              <div className="relative w-full flex-1 sm:max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por nome ou EAN..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent shadow-sm"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <span className="text-xs text-gray-500 sm:text-sm">
                {filteredAndSorted.length} de {products.length} produtos
              </span>
            </div>
          </div>

          <div className="space-y-3 px-3 pb-4 lg:hidden">
            {filteredAndSorted.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-10 text-center">
                <Search className="h-8 w-8 text-gray-300" />
                <p className="font-medium text-gray-500">Nenhum produto encontrado</p>
                <p className="text-sm text-gray-400">Tente ajustar a busca ou os filtros</p>
              </div>
            ) : (
              filteredAndSorted.map((product) => {
                const statusInfo = getStatusInfo(product.status);
                return (
                  <button
                    type="button"
                    key={`mobile-${cnpj}-${product.id}`}
                    onClick={() => onSelectProduct(product)}
                    className="w-full rounded-xl border border-gray-200 bg-white p-3 text-left shadow-sm transition-colors hover:border-teal-200 hover:bg-teal-50/30 focus:outline-none focus:ring-2 focus:ring-teal-300 sm:p-4"
                  >
                    <div className="flex min-w-0 items-start gap-3">
                      <ProductThumbnail src={product.foto} alt={product.nome} ean={product.ean} size="sm" details={{
                        status: product.status, statusLabel: product.statusLabel, estoqueAtual: product.estoqueAtual,
                        cmd: product.cmd, cobertura: product.cobertura, leadTime: product.leadTime,
                        estoqueAlvo: product.estoqueAlvo, pedidoSugerido: product.pedidoSugerido, fornecedor: product.fornecedor,
                      }} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-gray-900" title={product.nome}>{product.nome}</p>
                        <p className="mt-0.5 break-all text-xs text-gray-500">EAN: {product.ean}</p>
                        <ProductSignals product={product} />
                      </div>
                      <span className="flex-none">
                        <Badge label={statusInfo.label} color={statusInfo.color} bgColor={statusInfo.bgColor} />
                      </span>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <MobileMetric label="Estoque atual" value={formatNumber(product.estoqueAtual, 0)} />
                      <MobileMetric label="Cobertura" value={`${formatNumber(product.cobertura)}d`} />
                      <MobileMetric label="CMD" value={formatNumber(product.cmd)} />
                      <MobileMetric label="Pedido sugerido" value={formatNumber(product.pedidoSugerido, 0)} highlight />
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <div className="hidden overflow-x-visible lg:block">
            <table className="w-full table-fixed" style={{ minWidth: tableWidth }}>
              <colgroup>
                {(Object.keys(columnWidths) as SortField[]).map((field) => (
                  <col key={field} style={{ width: columnWidths[field] }} />
                ))}
              </colgroup>
              <thead
                className="sticky z-30 border-y border-gray-200 bg-gray-50 shadow-[0_2px_5px_rgba(15,23,42,0.10)]"
                style={{top: 'var(--kt-app-header-height, 65px)'}}
              >
                <tr>
                  <SortableHeader field="nome">Produto</SortableHeader>
                  <SortableHeader field="estoqueAtual" align="right">
                    <Tooltip content="Quantidade atual em estoque"><span className="cursor-help">Estoque Atual</span></Tooltip>
                  </SortableHeader>
                  <SortableHeader field="cmd" align="right">
                    <Tooltip content="Consumo Médio Diário"><span className="cursor-help">CMD</span></Tooltip>
                  </SortableHeader>
                  <SortableHeader field="ss" align="right">
                    <Tooltip content="Safety Stock: Estoque de Seguranca"><span className="cursor-help">SS</span></Tooltip>
                  </SortableHeader>
                  <SortableHeader field="rop" align="right">
                    <Tooltip content="Reorder Point: Ponto de Pedido"><span className="cursor-help">ROP</span></Tooltip>
                  </SortableHeader>
                  <SortableHeader field="cobertura" align="right">
                    <Tooltip content="Dias de cobertura: Estoque Atual / CMD"><span className="cursor-help">Cobertura (dias)</span></Tooltip>
                  </SortableHeader>
                  <SortableHeader field="estoqueAlvo" align="right">
                    <Tooltip content="Estoque alvo = CMD × (lead time + dias de cobertura alvo)."><span className="cursor-help">Estoque Alvo</span></Tooltip>
                  </SortableHeader>
                  <SortableHeader field="pedidoSugerido" align="right">
                    <Tooltip content="Quantidade sugerida para reposição"><span className="cursor-help">Pedido Sugerido</span></Tooltip>
                  </SortableHeader>
                  <SortableHeader field="status">Status</SortableHeader>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredAndSorted.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <Search className="w-8 h-8 text-gray-300" />
                        <p className="text-gray-500 font-medium">Nenhum produto encontrado</p>
                        <p className="text-gray-400 text-sm">Tente ajustar a busca ou os filtros</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredAndSorted.map((product) => {
                    const statusInfo = getStatusInfo(product.status);
                    return (
                      <tr
                        key={`${cnpj}-${product.id}`}
                        className="hover:bg-blue-50 transition-colors cursor-pointer"
                        onClick={() => onSelectProduct(product)}
                      >
                        <td className="overflow-hidden px-4 py-4">
                          <div className="flex items-center gap-3">
                            <ProductThumbnail src={product.foto} alt={product.nome} ean={product.ean} details={{
                              status: product.status, statusLabel: product.statusLabel, estoqueAtual: product.estoqueAtual,
                              cmd: product.cmd, cobertura: product.cobertura, leadTime: product.leadTime,
                              estoqueAlvo: product.estoqueAlvo, pedidoSugerido: product.pedidoSugerido, fornecedor: product.fornecedor,
                            }} />
                            <div className="min-w-0">
                              <div className="truncate font-medium text-gray-900">{product.nome}</div>
                              <div className="text-sm text-gray-500">EAN: {product.ean}</div>
                              <ProductSignals product={product} />
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-right font-medium text-gray-900">{product.estoqueAtual}</td>
                        <td className="px-4 py-4 text-right text-gray-900">{formatNumber(product.cmd)}</td>
                        <td className="px-4 py-4 text-right text-gray-900">{formatNumber(product.ss)}</td>
                        <td className="px-4 py-4 text-right text-gray-900">{formatNumber(product.rop)}</td>
                        <td className="px-4 py-4 text-right text-gray-900">{formatNumber(product.cobertura)}</td>
                        <td className="px-4 py-4 text-right text-gray-900">{formatNumber(product.estoqueAlvo)}</td>
                        <td className="px-4 py-4 text-right font-semibold text-blue-600">{formatNumber(product.pedidoSugerido, 0)}</td>
                        <td className="px-4 py-4">
                          <Badge label={statusInfo.label} color={statusInfo.color} bgColor={statusInfo.bgColor} />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const MobileMetric = ({ label, value, highlight = false }: { label: string; value: string; highlight?: boolean }) => (
  <span className="min-w-0 rounded-lg bg-gray-50 px-3 py-2">
    <span className="block truncate text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</span>
    <strong className={'mt-0.5 block truncate text-sm tabular-nums ' + (highlight ? 'text-blue-600' : 'text-gray-900')}>{value}</strong>
  </span>
);
